/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import cx from 'classnames';
import { Button } from '@/components/Button';
import Image, { pureLoader } from '@/components/NextImage';
import { LoadingIndicatorDots } from '@/components/LoadingIndicatorDots';
import Hidden from '@/components/Hidden';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useLocale } from '@/hooks/useLocale';
import { useLabels } from '@/hooks/useLabels';
import { countryCodes } from '@/constants/locale';
import styles from './styles.module.scss';

const labelMap = {
  lblAdd: 'label_MyAccountVehicle_maintenanceInterval_Add',
  lblAdded: 'label_added_to_cart_certona',
  lblOptions: 'label_certonaOptions',
};

type CertonaAddToCartButtonBaseProps = {
  onClick: () => void;
  ariaLabel: string;
  isLoading: boolean;
  isAdded: boolean;
  isOosOrDoesNotFit?: boolean;
  customClass?: string;
};

type CertonaAddToCartButtonProps =
  | (CertonaAddToCartButtonBaseProps & {
      showOptions: true;
      onOptionsClick: () => void;
      optionsAriaLabel?: string;
    })
  | (CertonaAddToCartButtonBaseProps & {
      showOptions?: false;
      onOptionsClick?: never;
      optionsAriaLabel?: never;
    });

export const CertonaAddToCartButton = ({
  onClick,
  onOptionsClick,
  ariaLabel,
  optionsAriaLabel,
  isLoading,
  isAdded,
  showOptions = false,
  isOosOrDoesNotFit = false,
  customClass,
}: CertonaAddToCartButtonProps) => {
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const locale = useLocale();
  const isMx = locale === countryCodes.mx;
  const { lblAdd, lblAdded, lblOptions } = useLabels(labelMap);

  return (
    <>
      <Hidden smDown implementation="js">
        {isOosOrDoesNotFit ? (
          <Button
            ariaLabel={showOptions ? optionsAriaLabel || ariaLabel : ariaLabel}
            variant="outlined"
            shape={isTablet ? 'rounded' : 'pill'}
            size="small"
            customClass={cx(
              isTablet ? styles.addtoCartButtonMobileForOos : styles.addtoCartButton,
              {
                [styles.configSkuMobileCTA]: showOptions && !isTablet,
                [styles.addedTocartButtonstyles]: isAdded && !isTablet,
              },
              customClass,
            )}
            onClick={showOptions ? onOptionsClick : onClick}
          >
            {!isLoading && showOptions && isDesktop && <span>{lblOptions.toUpperCase()}</span>}
            {!isLoading && (!showOptions || (showOptions && isTablet)) && !isAdded && (
              <>
                <Image
                  src={'/images/addcart.svg'}
                  alt=""
                  width={20}
                  height={20}
                  loader={pureLoader}
                />
                {!isLoading && !isAdded && !showOptions && !isTablet && (
                  <span className={cx(styles.addtoCartText, { [styles.addtoCartTextMx]: isMx })}>
                    {' '}
                    {lblAdd}
                  </span>
                )}
              </>
            )}
            {!isLoading && (!showOptions || (showOptions && isTablet)) && isAdded && (
              <>
                <Image
                  src={'/images/addedtocart.svg'}
                  alt=""
                  width={20}
                  height={20}
                  loader={pureLoader}
                />
                {!isLoading && isAdded && !showOptions && !isTablet && (
                  <span className={cx(styles.addtoCartText, { [styles.addtoCartTextMx]: isMx })}>
                    {' '}
                    {lblAdded}
                  </span>
                )}
              </>
            )}
            {isLoading && <LoadingIndicatorDots size={8} color={'#000'} />}
          </Button>
        ) : (
          <Button
            ariaLabel={ariaLabel}
            variant="outlined"
            shape="pill"
            size="small"
            customClass={cx(
              styles.addtoCartButton,
              { [styles.addedTocartButtonstyles]: isAdded },
              customClass,
            )}
            onClick={showOptions ? onOptionsClick : onClick}
          >
            {!isLoading && showOptions && <span>{lblOptions.toUpperCase()}</span>}
            {!isLoading && !isAdded && !showOptions && (
              <>
                <Image
                  src={'/images/addcart.svg'}
                  alt=""
                  width={15}
                  height={15}
                  loader={pureLoader}
                />
                <span className={cx(styles.addtoCartText, { [styles.addtoCartTextMx]: isMx })}>
                  {' '}
                  {lblAdd}
                </span>
              </>
            )}
            {!isLoading && isAdded && !showOptions && (
              <>
                <Image
                  src={'/images/green-tick-rounded.svg'}
                  alt=""
                  width={12}
                  height={12}
                  loader={pureLoader}
                />
                <span className={cx(styles.addtoCartText, { [styles.addtoCartTextMx]: isMx })}>
                  {' '}
                  {lblAdded}
                </span>
              </>
            )}
            {isLoading && <LoadingIndicatorDots size={12} color={'#000'} />}
          </Button>
        )}
      </Hidden>
      <Hidden mdUp implementation="js">
        {showOptions && !isOosOrDoesNotFit ? (
          <Button
            ariaLabel={optionsAriaLabel || ariaLabel}
            variant="outlined"
            shape="rounded"
            size="small"
            customClass={cx(styles.configSkuMobileCTA, customClass)}
            onClick={onOptionsClick}
          >
            <span>{lblOptions.toUpperCase()}</span>
          </Button>
        ) : (
          <Button
            ariaLabel={ariaLabel}
            variant="outlined"
            shape="rounded"
            size="small"
            customClass={cx(
              styles.addtoCartButtonMobile,
              { [styles.configSkuMobileCTA]: showOptions && isOosOrDoesNotFit },
              customClass,
            )}
            onClick={onClick}
            title={!isAdded ? lblAdd : lblAdded}
          >
            {!isLoading && !isAdded && (
              <Image
                src={'/images/addcart.svg'}
                alt=""
                width={20}
                height={20}
                loader={pureLoader}
              />
            )}
            {!isLoading && isAdded && (
              <Image
                src={'/images/addedtocart.svg'}
                alt=""
                width={20}
                height={20}
                loader={pureLoader}
              />
            )}
            {isLoading && <LoadingIndicatorDots size={8} color={'#000'} />}
          </Button>
        )}
      </Hidden>
    </>
  );
};
